import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { BackgroundColorPicker } from "./BackgroundColorPicker";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Zap, Bug, RefreshCw, RotateCcw, Lightbulb, Smartphone, Trash2, Sparkles, FileText, Map, Info, Heart, Share2, BarChart3 } from "lucide-react";
import { APP_VERSION, BUILD_HASH, BUILD_DATE } from "@/config/version";
import { Badge } from "@/components/ui/badge";
import { WormbleLogo } from "@/components/ui/WormbleLogo";
import { HintSystem } from "./HintSystem";
import { GameState } from "@/hooks/useWormbleGame";
import { lazy, Suspense, useState, useEffect, useRef } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NotificationSettings } from "./NotificationSettings";

// Lazy load debug panel (loads only when debug mode is active)
const DebugPanel = lazy(() => import("@/components/dev/DebugPanel").then(mod => ({ default: mod.DebugPanel })));
import { useNavigate } from "react-router-dom";
import { PWAStatusBadge } from "@/components/pwa/PWAStatusBadge";
import { InstallModal } from "@/components/pwa/InstallModal";
import { WhatsNewModal } from "@/components/pwa/WhatsNewModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePWA } from "@/hooks/usePWA";
import { trackPWAEvent } from "@/utils/pwaAnalytics";
import { clearAllCachesAndRefresh } from "@/utils/cacheUtils";
import { toast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hardMode: boolean;
  onHardModeToggle: () => void;
  hardModeDisabled: boolean;
  backgroundColor: string;
  onBackgroundColorChange: (color: string) => void;
  autoRevealSolution: boolean;
  onAutoRevealToggle: () => void;
  // Hint system props
  solution: string;
  guesses: string[];
  onHintUsed: (position: number, letter: string) => void;
  hintsUsed: number;
  onGiveUp: () => void;
  gameOver: boolean;
  // Debug
  gameState: GameState;
  showDebug: boolean;
  // Reset settings
  onResetSettings: () => void;
  // Debug toggle
  onDebugToggle: () => void;
  // PWA install
  onInstallClick?: () => void;
}

export function SettingsDialog({
  open,
  onOpenChange,
  hardMode,
  onHardModeToggle,
  hardModeDisabled,
  backgroundColor,
  onBackgroundColorChange,
  autoRevealSolution,
  onAutoRevealToggle,
  solution,
  guesses,
  onHintUsed,
  hintsUsed,
  onGiveUp,
  gameOver,
  gameState,
  showDebug,
  onResetSettings,
  onDebugToggle,
  onInstallClick
}: SettingsDialogProps) {
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { canInstall, isInstalled } = usePWA();
  const { isAdmin } = useAdminAuth();
  
  // Triple-tap tracking for debug mode
  const tapTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [tapCount, setTapCount] = useState(0);
  const [tapFlash, setTapFlash] = useState(false);
  
  // Prevent text selection on mouse down (fixes desktop triple-click)
  const handleSettingsTitleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Prevent double-click selection
  const handleSettingsTitleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Handle triple-tap on Settings title
  const handleSettingsTitleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Visual feedback flash
    setTapFlash(true);
    setTimeout(() => setTapFlash(false), 150);
    
    const newCount = tapCount + 1;
    setTapCount(newCount);
    
    // Clear existing timer
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }
    
    // Check if triple-tap reached
    if (newCount >= 3) {
      setTapCount(0);
      onDebugToggle();
      return;
    }
    
    // Set new timer - 800ms window is more forgiving
    tapTimerRef.current = setTimeout(() => {
      setTapCount(0);
    }, 800);
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    };
  }, []);
  
  // Tap counter display component
  const TapCounter = () => tapCount > 0 ? (
    <span className="ml-2 text-xs font-mono text-primary opacity-70 animate-fade-in">
      ({tapCount}/3)
    </span>
  ) : null;
  
  // Shared settings content renderer
  const renderSettingsContent = () => (
    <div className="space-y-4 py-4">
      {/* PWA Install Section - Prominent placement if not installed */}
      {canInstall && !isInstalled && onInstallClick && (
        <div className="bg-gradient-to-br from-primary/10 to-accent/5 border-2 border-primary/20 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base">Install Wormble</h3>
              <p className="text-xs text-muted-foreground">
                Get offline play, instant loading, and a native app feel
              </p>
            </div>
          </div>
          <Button 
            onClick={() => {
              trackPWAEvent('install_clicked', 'settings', canInstall, isInstalled);
              onInstallClick?.();
            }} 
            className="w-full gap-2"
          >
            <Smartphone className="h-4 w-4" />
            Install Now
          </Button>
        </div>
      )}

      {/* Game Assistance - Always visible */}
      <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h3 className="font-semibold text-base">Need Help?</h3>
        </div>
        {!gameOver && guesses.length > 0 ? (
          <HintSystem
            solution={solution}
            guesses={guesses}
            onHintUsed={onHintUsed}
            hintsUsed={hintsUsed}
            maxHints={3}
            onGiveUp={onGiveUp}
          />
        ) : !gameOver && guesses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            Make your first guess to unlock hints and other options.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">
            Hints are available during active games. Play a new puzzle to use hints!
          </p>
        )}
      </div>

      {/* Game Settings */}
      <div className="space-y-4">
        <h3 className="font-medium text-base">Game Settings</h3>
        
        {/* Hard Mode */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium">Hard Mode</h3>
          <p className="text-sm text-muted-foreground">
            Any revealed hints must be used in subsequent guesses
          </p>
        </div>
        <div className="flex-shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant={hardMode ? "default" : "outline"}
                    size="sm"
                    onClick={onHardModeToggle}
                    disabled={hardModeDisabled}
                    className="gap-2"
                  >
                    <Zap className={`h-4 w-4 ${hardMode ? 'text-primary-foreground' : 'text-orange-500'}`} />
                    {hardMode ? 'On' : 'Off'}
                  </Button>
                </span>
              </TooltipTrigger>
              {hardModeDisabled && (
                <TooltipContent>
                  <p>Hard mode can only be changed before your first guess</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

        {/* Auto-Reveal Solution */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium">Auto-Reveal Solution</h3>
            <p className="text-sm text-muted-foreground">
              Show the answer automatically when you lose
            </p>
          </div>
          <div className="flex-shrink-0">
            <Switch 
              checked={autoRevealSolution}
              onCheckedChange={onAutoRevealToggle}
              aria-label="Toggle auto reveal solution"
            />
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="pt-2 border-t space-y-4">
        <NotificationSettings />
      </div>

      {/* Visual Settings */}
      <div className="pt-2 border-t space-y-4">
        <h3 className="font-medium text-base">Visual Settings</h3>
        
        {/* Theme */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium">Theme</h3>
          <p className="text-sm text-muted-foreground">
            Choose your preferred color scheme
          </p>
        </div>
        <div className="flex-shrink-0">
          <ThemeSwitcher />
        </div>
      </div>

        {/* Background Color */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium">Background Color</h3>
            <p className="text-sm text-muted-foreground">
              Customize your game background
            </p>
          </div>
          <div className="flex-shrink-0">
            <BackgroundColorPicker 
              currentColor={backgroundColor}
              onColorChange={onBackgroundColorChange}
            />
          </div>
        </div>

      </div>

      {/* About Wormble */}
      <div className="pt-2 border-t space-y-3">
        <h3 className="font-medium text-base">About Wormble</h3>
        <div className="bg-gradient-to-br from-muted/50 to-muted/20 rounded-lg p-4 border">
          <div className="flex items-center gap-3 mb-3">
            <WormbleLogo size="sm" linkTo={null} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-xs font-mono">
                  v{APP_VERSION}
                </Badge>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground font-mono cursor-help flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        {BUILD_HASH.slice(0, 7)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Build: {BUILD_HASH}</p>
                      <p className="text-xs text-muted-foreground">{BUILD_DATE}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWhatsNew(true)}
              className="gap-1.5 flex-1"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              What's New
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onOpenChange(false);
                navigate('/changelog');
              }}
              className="gap-1.5 flex-1"
            >
              <FileText className="h-3.5 w-3.5" />
              Changelog
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onOpenChange(false);
                navigate('/roadmap');
              }}
              className="gap-1.5 flex-1"
            >
              <Map className="h-3.5 w-3.5" />
              Roadmap
            </Button>
          </div>
          
          <Button
            variant="default"
            size="sm"
            onClick={async () => {
              const shareText = "Check out Wormble - a daily word puzzle game! 🐛\n\nhttps://wormble.com";
              if (navigator.share) {
                try {
                  await navigator.share({ title: "Wormble", text: shareText });
                } catch (err) {
                  if ((err as Error).name !== 'AbortError') {
                    await navigator.clipboard.writeText(shareText);
                    toast({ title: "Link copied!", description: "Share Wormble with your friends" });
                  }
                }
              } else {
                await navigator.clipboard.writeText(shareText);
                toast({ title: "Link copied!", description: "Share Wormble with your friends" });
              }
            }}
            className="w-full gap-2 mt-3"
          >
            <Share2 className="h-4 w-4" />
            Share Wormble with Friends
          </Button>
          
          <div className="mt-3 pt-3 border-t border-border/50 text-center">
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              Made with <Heart className="h-3 w-3 text-red-500 fill-red-500" /> by Wormble Team
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">© 2024 Wormble</p>
          </div>
        </div>
      </div>

      {/* Admin Section - Only visible to admins */}
      {isAdmin && (
        <div className="pt-2 border-t space-y-2">
          <h3 className="font-medium text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            Admin
          </h3>
          <Button
            variant="default"
            onClick={() => {
              onOpenChange(false);
              navigate('/admin');
            }}
            className="w-full gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            Analytics Dashboard
          </Button>
        </div>
      )}

      {/* App Management */}
      <div className="pt-2 border-t space-y-2">
        <h3 className="font-medium text-base">App Management</h3>
        <Button
          variant="outline"
          onClick={() => setShowInstallModal(true)}
          className="w-full justify-between p-3 h-auto hover:bg-accent"
        >
          <span className="text-sm font-medium">App Status</span>
          <PWAStatusBadge />
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            window.location.reload();
          }}
          className="w-full gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh App
        </Button>
        <Button
          variant="outline"
          onClick={async () => {
            toast({
              title: "Clearing cache...",
              description: "The app will refresh with fresh code.",
            });
            await clearAllCachesAndRefresh();
          }}
          className="w-full gap-2 text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
          Clear Cache & Refresh
        </Button>
        <Button
          variant="outline"
          onClick={onResetSettings}
          className="w-full gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Reset All Settings
        </Button>
      </div>

      {/* Debug Button (Dev Mode Only) */}
      {showDebug && (
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="w-full gap-2"
          >
            <Bug className="h-4 w-4" />
            {showDebugPanel ? "Hide Debug Panel" : "Show Debug Panel"}
          </Button>
        </div>
      )}

      {/* Debug Panel (embedded in dialog) */}
      {showDebug && showDebugPanel && (
        <div className="pt-4">
          <Suspense fallback={<div className="text-center text-sm text-muted-foreground py-4">Loading debug tools...</div>}>
            <DebugPanel gameState={gameState} embedded />
          </Suspense>
        </div>
      )}
      
      {/* Install Modal */}
      <InstallModal 
        open={showInstallModal} 
        onOpenChange={setShowInstallModal}
        isOnline={navigator.onLine}
      />
      
      {/* What's New Modal */}
      <WhatsNewModal 
        forceOpen={showWhatsNew} 
        onClose={() => setShowWhatsNew(false)} 
      />
    </div>
  );
  
  // Mobile: Render Drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle 
              onClick={handleSettingsTitleClick}
              onMouseDown={handleSettingsTitleMouseDown}
              onDoubleClick={handleSettingsTitleDoubleClick}
              className={`cursor-pointer select-none transition-colors duration-150 flex items-center ${tapFlash ? 'text-primary' : ''}`}
              style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
            >
              Settings
              <TapCounter />
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">
            {renderSettingsContent()}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  
  // Desktop: Render Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden" aria-describedby="settings-description">
        <DialogHeader>
          <DialogTitle 
            onClick={handleSettingsTitleClick}
            onMouseDown={handleSettingsTitleMouseDown}
            onDoubleClick={handleSettingsTitleDoubleClick}
            className={`cursor-pointer select-none transition-colors duration-150 flex items-center ${tapFlash ? 'text-primary' : ''}`}
            style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          >
            Settings
            <TapCounter />
          </DialogTitle>
          <p id="settings-description" className="sr-only">
            Customize your game preferences including hard mode, theme, background color, and game assistance options.
          </p>
        </DialogHeader>
        {renderSettingsContent()}
      </DialogContent>
    </Dialog>
  );
}
